import creditRatios from '@klv/shared/pricing/static/credit-ratio-v2.json';
import smsCreditPricing from '@klv/shared/pricing/static/sms-credit-pricing.json';

const countriesData = creditRatios.countries;

type TCreditType = 'sms' | 'mms' | 'whatsapp-marketing' | 'whatsapp-transactional';

const getCreditRatio = (countryCode: string, creditType: TCreditType): number => {
  return countriesData[countryCode as keyof typeof countriesData]?.[creditType] || 0;
};

/**
 * Calculate required credit tier based on messages and country
 * @param messages - Number of messages to send
 * @param countryCode - Country code (e.g., 'SE', 'US')
 * @param creditType - Type of credit ('sms', 'mms', 'whatsapp-marketing', 'whatsapp-transactional')
 * @returns The required credit tier amount
 */
export const calculateRequiredCreditTier = (
  messages: number, 
  countryCode: string, 
  creditType: TCreditType
): number => {
  // Get the appropriate ratio for the country and credit type
  const ratio = getCreditRatio(countryCode, creditType) || 1;
  
  // Calculate required credits
  const requiredCredits = messages * ratio;
  
  // Derive tiers from pricing file keys and sort them
  const tiers = Object.keys(smsCreditPricing.credit_pricing)
    .map(tier => parseInt(tier, 10))
    .sort((a, b) => a - b);
  
  // Find the next higher tier (or exact match)
  const tierIndex = tiers.findIndex(tier => tier >= requiredCredits);
  
  // Return the appropriate tier, or the highest tier if none found
  return tierIndex >= 0 ? tiers[tierIndex] : tiers[tiers.length - 1];
};

/**
 * Get the price for a given credit tier
 * @param creditTier - The credit tier amount
 * @returns The price for the credit tier in USD
 */
export const getPriceForCreditTier = (creditTier: number): number => {
  return smsCreditPricing.credit_pricing[creditTier.toString() as keyof typeof smsCreditPricing.credit_pricing] || 0;
};

/**
 * Calculate maximum number of sends based on credit tier and country ratio
 * @param creditTier - The credit tier amount
 * @param countryCode - Country code (e.g., 'SE', 'US')
 * @param creditType - Type of credit ('sms', 'mms', 'whatsapp-marketing', 'whatsapp-transactional')
 * @returns The maximum number of sends possible
 */
export const calculateMaxSends = (
  creditTier: number, 
  countryCode: string, 
  creditType: TCreditType
): number => {
  // Get the appropriate ratio for the country and credit type
  const ratio = getCreditRatio(countryCode, creditType) || 1;
  
  // Calculate max sends: creditTier / ratio
  return Math.floor(creditTier / ratio);
};

/**
 * Calculate price per send based on credit tier, price, and country ratio
 * @param creditTier - The credit tier amount
 * @param countryCode - Country code (e.g., 'SE', 'US')
 * @param creditType - Type of credit ('sms', 'mms', 'whatsapp-marketing', 'whatsapp-transactional')
 * @returns The price per send in USD
 */
export const calculatePricePerSend = (
  creditTier: number, 
  countryCode: string, 
  creditType: TCreditType
): number => {
  const price = getPriceForCreditTier(creditTier);
  const maxSends = calculateMaxSends(creditTier, countryCode, creditType);
  
  // Avoid division by zero
  if (maxSends === 0 || price === 0) {
    return 0;
  }
  
  return price / maxSends;
};
